import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Package, Download, Sparkles, BookOpen, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Marketplace() {
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [lockingId, setLockingId] = useState("");
  const [macAddress, setMacAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/marketplace");
    setPlugins(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openTrial = (p) => {
    setSelected(p);
    setLockingId("");
    setMacAddress("");
  };

  const submitTrial = async () => {
    if (!lockingId.trim() || !macAddress.trim()) {
      toast.error("Enter your Locking ID and MAC address");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/licenses/trial", {
        plugin_id: selected.id,
        locking_id: lockingId.trim(),
        mac_address: macAddress.trim(),
      });
      toast.success(`Trial generated for ${selected.name}`);
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const download = async (p) => {
    try {
      const res = await api.get(`/plugins/${p.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = p.plugin_file_name || `${p.slug}.qplug`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      toast.error("Download failed — an active license is required");
    }
  };

  return (
    <AppShell
      title="Marketplace"
      subtitle="Browse LifeLike plugins for Q-SYS. Start a trial bound to your Core, then download once licensed."
    >
      {loading ? (
        <div className="font-mono text-sm text-orange-500 animate-pulse">[ loading catalog... ]</div>
      ) : plugins.length === 0 ? (
        <div data-testid="marketplace-empty" className="border border-dashed border-zinc-800 rounded-md p-12 text-center text-zinc-500 font-mono text-sm">
          [ no published plugins yet ]
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plugins.map((p) => (
            <Card key={p.id} data-testid={`market-card-${p.slug}`} className="bg-zinc-950 border-zinc-800 rounded-md overflow-hidden hover:border-zinc-700 hover:-translate-y-px transition-all flex flex-col">
              {p.cover_image_id ? (
                <img src={`${API}/files/${p.cover_image_id}`} alt={p.name} className="h-40 w-full object-cover border-b border-zinc-800" />
              ) : (
                <div className="h-40 w-full border-b border-zinc-800 bg-zinc-900 flex items-center justify-center">
                  <Package className="h-10 w-10 text-zinc-700" />
                </div>
              )}
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {p.category && <Badge className="rounded-sm border border-zinc-700 bg-zinc-800 text-zinc-300 text-[10px] font-mono uppercase tracking-widest">{p.category}</Badge>}
                  {p.has_license && (
                    <span data-testid={`market-licensed-${p.slug}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" /> Licensed
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold tracking-tight">{p.name}</h3>
                <p className="font-mono text-xs text-orange-500 mt-0.5">v{p.version}{p.price != null ? ` · $${p.price}` : ""}</p>
                <p className="text-sm text-zinc-400 mt-3 flex-1">{p.long_description || p.description}</p>

                {p.screenshot_ids?.length > 0 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                    {p.screenshot_ids.map((sid) => (
                      <img
                        key={sid}
                        src={`${API}/files/${sid}`}
                        alt="screenshot"
                        onClick={() => setLightbox(`${API}/files/${sid}`)}
                        data-testid={`market-shot-${sid}`}
                        className="h-16 w-24 shrink-0 object-cover rounded-sm border border-zinc-800 cursor-pointer hover:border-orange-500 transition-colors"
                      />
                    ))}
                  </div>
                )}

                {p.docs_url && (
                  <a href={p.docs_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-mono text-zinc-400 hover:text-orange-400 transition-colors mt-3">
                    <BookOpen className="h-3.5 w-3.5" /> Documentation
                  </a>
                )}

                <div className="flex gap-2 mt-5">
                  <Button
                    data-testid={`market-trial-${p.slug}`}
                    onClick={() => openTrial(p)}
                    className="flex-1 rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white font-semibold"
                  >
                    <Sparkles className="h-4 w-4 mr-1.5" /> Trial
                  </Button>
                  {p.has_file && (
                    <Button
                      data-testid={`market-download-${p.slug}`}
                      onClick={() => download(p)}
                      variant="outline"
                      disabled={!p.has_license}
                      title={p.has_license ? "Download plugin" : "Requires an active license"}
                      className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white disabled:opacity-40"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Trial dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Start trial · {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              A signed {selected?.trial_days}-day trial, bound to your Locking ID and MAC address.
            </p>
            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Locking ID</Label>
              <Input data-testid="market-trial-locking-input" value={lockingId} onChange={(e) => setLockingId(e.target.value)} placeholder="QSC-CORE110F-..." className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">MAC Address</Label>
              <Input data-testid="market-trial-mac-input" value={macAddress} onChange={(e) => setMacAddress(e.target.value)} placeholder="00:1A:2B:3C:4D:5E" className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="market-trial-submit" onClick={submitTrial} disabled={submitting} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white font-semibold">
              {submitting ? "Signing..." : "Generate trial"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screenshot lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl p-2">
          <DialogHeader className="sr-only"><DialogTitle>Screenshot preview</DialogTitle></DialogHeader>
          {lightbox && <img src={lightbox} alt="screenshot" data-testid="market-lightbox-img" className="w-full rounded-sm" />}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
